import React, {useEffect, useState} from "react"
import {useSessionCookie} from "../hooks/useSessionCookie";
import {isSubscriptionExpired} from "../utils/subscription";
import {createContext} from "react";
import {useAsync} from "../hooks/useAsyncResult";
import {getActiveSubscription} from "../services/api/subscription";
import {Subscription} from "../models/subscription";
import PricingDialog from "../components/PricingDialog";
import {useLocation, useSearchParams} from "react-router-dom";
import {APP_PREFIX, appRoute} from "../utils/routes";

export const ISubscriptionContext = createContext<SubscriptionContextState>({})

export type SubscriptionContextState = {
    loading?: boolean
    subscription?: Subscription,
    showSubscriptionDialog?: boolean,
    setShowSubscriptionDialog?: (show: boolean) => void,
    hasAccess?: boolean,
    stickyPlansDialog?: boolean,
    setStickyPlansDialog?: (show: boolean) => void,
}


const SubscriptionContext = (props: { children }) => {
    const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false)
    const [stickyPlansDialog, setStickyPlansDialog] = useState(false)
    const [hasAccess, setHasAccess] = useState(true)
    const activeSubscriptionAsyncResult = useAsync(getActiveSubscription, true, true, true)
    const location = useLocation()
    const {userSession} = useSessionCookie()
    // The app's session, not Firebase's: Firebase signs in before the session exists (and, on a
    // first social login, before the user record does), so it can't gate authenticated calls.
    const sessionToken = userSession?.token
    const PROTECTED_ROUTES: (string | RegExp)[] = [
        new RegExp(`^${APP_PREFIX}/practice/(reading|listening|writing|speaking)$`),
        appRoute("resources"),
    ]

    // The endpoint answers with the subscription or a `{data}` wrapper — and the payload is reset to
    // null on logout while still flagged as loaded, so `"data" in payload` crashed the app then.
    const activeSubscription = (): Subscription | null => {
        const payload = activeSubscriptionAsyncResult.payload as Subscription | { data?: Subscription } | null
        if (!payload || typeof payload !== "object") return null
        return "data" in payload ? payload.data ?? null : payload as Subscription
    }

    useEffect(() => {
        if (sessionToken)
            activeSubscriptionAsyncResult.execute()
        else
            activeSubscriptionAsyncResult.setData(null)
    }, [sessionToken])

    useEffect(() => {
        if (activeSubscriptionAsyncResult.isSuccess && PROTECTED_ROUTES.some((route: any) =>
            route instanceof RegExp ? route.test(window.location.pathname) : window.location.pathname.startsWith(route)
        )) {
            const payload = activeSubscription()
            // Access runs for the plan's validity in days from when it started (see utils/subscription).
            const active = !!payload && !isSubscriptionExpired(payload)
            setHasAccess(active)
            setShowSubscriptionDialog(!active)
            setStickyPlansDialog(!active)
        } else if (activeSubscriptionAsyncResult.isSuccess) {
            const payload = activeSubscription()
            setHasAccess(!!payload && !isSubscriptionExpired(payload))
            setShowSubscriptionDialog(false)
        } else {
            setHasAccess(false)
            setStickyPlansDialog(false)
            setShowSubscriptionDialog(false)
        }
    }, [activeSubscriptionAsyncResult.isSuccess, location])

    return <ISubscriptionContext.Provider value={{
        loading: activeSubscriptionAsyncResult.loading,
        subscription: activeSubscriptionAsyncResult.payload,
        showSubscriptionDialog,
        setShowSubscriptionDialog,
        hasAccess,
        stickyPlansDialog,
        setStickyPlansDialog
    }}>
        {props.children}
        <PricingDialog sticky={stickyPlansDialog} open={showSubscriptionDialog}
                       onClose={() => {
                           if (!stickyPlansDialog)
                               setShowSubscriptionDialog(false)
                       }}/>
    </ISubscriptionContext.Provider>
}

export default SubscriptionContext